import React from 'react';
import DynamicTable from "../../../../widgets/dynamicTable/DynamicTable";
import HelperMetiers360 from "../../../../../services/HelpersMetiers360.js";
import PropTypes from 'prop-types';
import { Alert, Badge, Button } from 'react-bootstrap';
import Webapp2UsersFilters from './Webapp2UsersFilters.jsx';
import AuthorizationChecker from '../../../../../services/AuthorizationChecker.js';
import LogsComponent from '../../../../logs/LogsComponent.jsx';

const Webapp2UsersTable = ({
    users,
    groups,
    handleClick,
    valueInitSort = "lastActivityAt",
    tableName = "webapp2UsersTable",
    filename,
    actionButton,
    noResultMessage = "Aucun résultat pour les filtres sélectionnés.",
    showEditButton,
    showGroupsButton,
    showLogsButton,
    onEditClick,
    onGroupsClick,
    displaySearchBar = true,
}) => {
    // sortType is defined inside the render prop so that it is a fresh reference on every
    // call. This ensures DynamicTable's useMemo (which has contentSort in its deps but not
    // contentTable) re-runs whenever filters change, preventing stale row display.
    const buildSortType = () => users?.length > 0
        && [
            {
                value: 'userName', label: 'Pseudo',
                test: HelperMetiers360.isArrayContainsValue(users, "userName"),
                method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.userName, b.userName),
                display: (user) => <>{user.userName ?? '-'}{user.isArchived && <Badge className="ms-2 background-orange">Archivé</Badge>}</>,
                flatDisplay: (user) => user.userName ?? '-'
            },
            {
                value: 'firstName', label: 'Prénom',
                test: HelperMetiers360.isArrayContainsValue(users, "firstName"),
                method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.firstName, b.firstName),
                display: (user) => user.firstName ?? '-'
            },
            {
                value: 'lastName', label: 'Nom',
                test: HelperMetiers360.isArrayContainsValue(users, "lastName"),
                method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.lastName, b.lastName),
                display: (user) => user.lastName ?? '-'
            },
            {
                value: 'email', label: 'Email',
                test: HelperMetiers360.isArrayContainsValue(users, "email"),
                method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.email, b.email),
                display: (user) => user.email ?? '-'
            },
            {
                value: 'webapp2Groups', label: 'Groupes',
                test: groups?.length > 0,
                method: (a, b) => HelperMetiers360.localeCompareWithNullable(
                    a.webapp2Groups?.[0]?.name,
                    b.webapp2Groups?.[0]?.name
                ),
                display: (user) => user.webapp2Groups?.length > 0
                    ? user.webapp2Groups.map(g => g.name).join(', ')
                    : '-',
                flatDisplay: (user) => user.webapp2Groups?.map(g => g.name).join(', ') ?? '-'
            },
            // For the moment we don't display this information as all users are invited
            // {
            //     value: 'authenticated', label: 'Type de session',
            //     test: HelperMetiers360.isArrayContainsValue(users, "isAuthenticated"),
            //     method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.isAuthenticated, b.isAuthenticated),
            //     display: (user) => user.isAuthenticated ? 'Authentifiée' : 'Invitée'
            // },
            {
                value: 'createdAt',
                label: 'Date de création',
                test: HelperMetiers360.isArrayContainsValue(users, "createdAt"),
                method: (a, b) => HelperMetiers360.dateCompareWithNullableDescending(a.createdAt?.date, b.createdAt?.date),
                display: (user) => user.createdAt ? HelperMetiers360.getdisplayDateType(user.createdAt?.date) : '-'
            },
            {
                value: 'lastActivityAt', label: 'Dernière connexion',
                test: true,
                method: (a, b) => HelperMetiers360.dateCompareWithNullableDescending(a.lastActivityAt, b.lastActivityAt),
                display: (user) => user.lastActivityAt ? HelperMetiers360.getdisplayDateType(user.lastActivityAt) : '-'
            },
            // This entry is probably confusing because it's not clear what it represents for the user
            // {
            //     value: 'updatedAt', label: 'Dernière action',
            //     test: HelperMetiers360.isArrayContainsValue(users, "updatedAt"),
            //     method: (a, b) => HelperMetiers360.dateCompareWithNullableDescending(a.updatedAt?.date, b.updatedAt?.date),
            //     display: (user) => user?.updatedAt?.date ? HelperMetiers360.getdisplayDateType(user.updatedAt?.date, 'day') : '-'
            // },
            {
                value: 'storedActivitiesCount', label: 'Nb activités uniques réalisées',
                test: users?.length > 0,
                method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.storedActivitiesCount, b.storedActivitiesCount),
                display: (stat) => stat.storedActivitiesCount
            },
            {
                value: 'currentActivitiesCount', label: 'Nb activités en cours',
                test: users?.length > 0,
                method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.currentActivitiesCount, b.currentActivitiesCount),
                display: (stat) => stat.currentActivitiesCount
            },
            ...(actionButton ? [{
                value: 'action', label: '',
                test: true,
                display: (user) => actionButton(user),
                flatDisplay: () => ''
            }] : []),
            {
                value: 'actions', label: '',
                test: !!showEditButton || !!showGroupsButton,
                display: (user) => <div className="d-flex gap-1 justify-content-center">
                    {showEditButton && <Button variant="secondary" size="sm" className="m-0 p-2"
                        onClick={(e) => { e.stopPropagation(); onEditClick(user); }}>
                        <i className="fas fa-edit"></i>&nbsp;Infos
                    </Button>}
                    {showGroupsButton && <Button variant="secondary" size="sm" className="m-0 p-2"
                        onClick={(e) => { e.stopPropagation(); onGroupsClick(user); }}>
                        <i className="fas fa-users"></i>&nbsp;Groupes
                    </Button>}
                </div>,
                flatDisplay: () => ''
            },
            {
                value: 'actionLogs', label: 'Logs',
                test: !!showLogsButton && AuthorizationChecker.isAdmin(),
                display: (user) => <LogsComponent entityNameFromComponent="webapp2User" uniqueIdFromComponent={user.uniqueId} />,
                flatDisplay: () => null
            }
        ];

    return <Webapp2UsersFilters users={users} groups={groups} idPrefix={tableName} displaySearchBar={displaySearchBar}>
        {(filteredUsers) => filteredUsers?.length > 0
            ? <DynamicTable
                contentTable={filteredUsers.map(u => u.isArchived ? { ...u, background: '#fac974' /* $orange-background from colors.scss */ } : u)}
                contentSort={buildSortType()}
                handleClick={handleClick}
                valueInitSort={valueInitSort}
                index='uniqueId'
                tableName={tableName}
                filename={filename}
            />
            : <Alert variant='warning'>{noResultMessage}</Alert>
        }
    </Webapp2UsersFilters>;
};

Webapp2UsersTable.propTypes = {
    users: PropTypes.array,
    groups: PropTypes.array,
    handleClick: PropTypes.func,
    valueInitSort: PropTypes.string,
    tableName: PropTypes.string,
    filename: PropTypes.string,
    actionButton: PropTypes.func,
    noResultMessage: PropTypes.string,
    showEditButton: PropTypes.bool,
    showGroupsButton: PropTypes.bool,
    showLogsButton: PropTypes.bool,
    onEditClick: PropTypes.func,
    onGroupsClick: PropTypes.func,
    displaySearchBar: PropTypes.bool,
};

export default Webapp2UsersTable;
